import { FightState } from 'web/redux/slices/fight-slice'
import { MetaState } from 'web/redux/slices/meta-slice'
import { RosterState } from 'web/redux/slices/roster-slice'
import { SetupState } from 'web/redux/slices/setup-slice'
import { assertInvariant } from './utils'
import { EditFightState } from 'web/redux/slices/edit-fight-slice'
import { EditSetupState } from 'web/redux/slices/edit-setup-slice'

export const LOCALSTORAGE_STATE_KEY = "application-state-v1"
const DEBUG_STATE_KEY = "RAIDCDS-DEBUG-SAVED-STATE"

type Version = { localStorageVersion: string }

/**
 * You must assume this is always stored when the user loads the
 * application.
 *
 * If we make breaking changes we also need to add a migration step
 * and create a new version format.
 *
 * TODO: Building up the type like this via type references is
 * _EXTREMELY_ unsafe, as any breaking changes you make to the types
 * will be silently accepted by the compiler and hidden.
 *
 * To make this secure, we'd have to generate a completely separate
 * type signature, and preferably use a framework like Zod to strictly
 * validate the data on load.
 */
export type SerializationState_v1 = {
    meta?: MetaState
    fight?: FightState
    setups?: SetupState
    roster?: RosterState
    editFight?: EditFightState
    editSetup?: EditSetupState
}

export const Storage = {

    writeState: (state: SerializationState_v1) => {

        const v1State: SerializationState_v1 & Version = {
            ...state,
            localStorageVersion: "v1"
        }

        window.localStorage.setItem(
            LOCALSTORAGE_STATE_KEY,
            JSON.stringify(v1State, null, "  ")
        )
    },

    readState: (): SerializationState_v1 & Version => {
        const maybeJson = window.localStorage.getItem(LOCALSTORAGE_STATE_KEY)

        if (maybeJson == null) {
            return { localStorageVersion: "v1" }
        }

        return JSON.parse(maybeJson)
    },

    readDebugState: (): SerializationState_v1 & Version => {
        assertInvariant(process.env.NODE_ENV === 'development')

        const maybeJson = window.localStorage.getItem(DEBUG_STATE_KEY)

        if (maybeJson == null) {
            throw Error(`No debug state stored, aborting`)
        }

        return JSON.parse(maybeJson)
    },

    writeDebugState: (state: SerializationState_v1) => {
        assertInvariant(process.env.NODE_ENV === 'development')

        const v1State: SerializationState_v1 & Version = {
            ...state,
            localStorageVersion: "v1"
        }

        window.localStorage.setItem(
            DEBUG_STATE_KEY,
            JSON.stringify(v1State, null, "  ")
        )
    },

    dangerous_nukeState: () => {
        assertInvariant(process.env.NODE_ENV === 'development')

        window.localStorage.removeItem(LOCALSTORAGE_STATE_KEY)
    },

}
